import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setSelectedTransaction } from "../../redux/admin/investmentTransactions.slice";
import { fetchInvestmentTransactions, addInvestmentTransaction, updateInvestmentTransaction, deleteInvestmentTransaction } from "../../redux/admin/investmentTransactions.thunk";
import { fetchInvestments } from "../../redux/admin/investments.thunk";
import { fetchInvestors } from "../../redux/admin/investor.thunk";
import { fetchBranches } from "../../redux/admin/branch.thunk";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "sm-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton shimmer ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "sm-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

const InvestmentTransactions = () => {
  const dispatch = useDispatch();
  const limit = 10;

  /* =====================================================
     REDUX SELECTORS
  ====================================================== */
  const txState = useSelector((state) => state.admin?.investmentTransactions || {});
  const investmentState = useSelector((state) => state.admin?.investments || {});
  const investorState = useSelector((state) => state.admin?.investors || {});
  const branchState = useSelector((state) => state.admin?.branches || {});

  const globalInvestments = investmentState.investments || investmentState.data || [];
  const globalInvestors = investorState.investors || investorState.data || [];
  const globalBranches = branchState.branches || branchState.branchList || branchState.data || [];

  const {
    transactions = [],
    totalPages = 1,
    currentPage = 1,
    total = 0,
    loading = false,
    selectedTransaction = null,
  } = txState;

  /* =====================================================
     SEARCH & FILTER STATES
  ====================================================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");

  /* =====================================================
     MODAL & TABS STATE
  ====================================================== */
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [saving, setSaving] = useState(false);

  /* =====================================================
     INITIAL FORM STATE
  ====================================================== */
  const initialFormState = {
    transaction_ref: "",
    investment_id: "",
    investor_id: "",
    branch_id: "",
    transaction_type: "investment",
    amount: "0.00",
    principal_amount: "0.00",
    interest_amount: "0.00",
    profit_amount: "0.00",
    loss_amount: "0.00",
    installment_no: "",
    transaction_date: "",
    payment_method: "",
    account_no: "",
    reference_no: "",
    description: "",
    status: "completed"
  };

  const [formData, setFormData] = useState(initialFormState);

  /* =====================================================
     SEARCH DEBOUNCE EFFECT
  ====================================================== */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* =====================================================
     FETCH LIFECYCLE MANAGEMENT
  ====================================================== */
  useEffect(() => {
    dispatch(fetchInvestmentTransactions({ 
      page: currentPage, 
      limit, 
      search: debouncedSearch, 
      transaction_type: typeFilter,
      status: statusFilter, 
      branch_id: branchFilter 
    }));
  }, [dispatch, currentPage, debouncedSearch, typeFilter, statusFilter, branchFilter]);

  useEffect(() => {
    dispatch(fetchInvestments({ page: 1, limit: 1000 }));
    dispatch(fetchInvestors({ page: 1, limit: 1000, search: "", status: "active" }));
    dispatch(fetchBranches({ page: 1, limit: 1000 }));
  }, [dispatch]);

  /* =====================================================
     HANDLERS & HELPER FUNCTIONS
  ====================================================== */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) dispatch(setCurrentPage(newPage));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const openAddModal = () => {
    setIsEditMode(false);
    
    // Auto-generate sequential transaction reference starting from total + 1 (e.g., INVTXN-2026-0002)
    const currentYear = new Date().getFullYear();
    const nextSeq = String(total + 1).padStart(4, '0');
    const generatedRef = `INVTXN-${currentYear}-${nextSeq}`;

    setFormData({
      ...initialFormState,
      transaction_ref: generatedRef,
      transaction_date: new Date().toISOString().split('T')[0]
    });
    setActiveTab("general");
    setShowModal(true);
  };

  const openEditModal = (tx) => {
    setIsEditMode(true);
    dispatch(setSelectedTransaction(tx));
    setFormData({ 
      ...initialFormState, 
      ...tx,
      installment_no: tx.installment_no || "",
      status: tx.status || "completed"
    });
    setActiveTab("general");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.transaction_ref || !formData.investment_id || !formData.investor_id || !formData.branch_id || !formData.amount || !formData.transaction_date) {
      toastr.warning("Transaction Ref, Investment, Investor, Branch, Amount, and Date are required");
      return;
    }

    setSaving(true);
    try {
      let payload = { ...formData };

      if (isEditMode) {
        await dispatch(updateInvestmentTransaction({ id: selectedTransaction.id, transactionData: payload })).unwrap();
        toastr.success("Transaction Record Updated");
      } else {
        await dispatch(addInvestmentTransaction(payload)).unwrap();
        toastr.success("Transaction Logged Successfully");
      }
      setShowModal(false);
      dispatch(fetchInvestmentTransactions({ page: currentPage, limit, search: debouncedSearch, transaction_type: typeFilter, status: statusFilter, branch_id: branchFilter }));
    } catch (err) { 
      toastr.error(err || "Process Failed"); 
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this transaction ledger entry?")) return;
    try {
      await dispatch(deleteInvestmentTransaction(id)).unwrap();
      toastr.success("Transaction deleted successfully");
      dispatch(fetchInvestmentTransactions({ page: currentPage, limit, search: debouncedSearch, transaction_type: typeFilter, status: statusFilter, branch_id: branchFilter }));
    } catch (err) {
      toastr.error(err || "Failed to delete");
    }
  };

  const renderPagination = () =>
    Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => handlePageChange(i)}
        className={`sm-page-btn${currentPage === i ? " active" : ""}`}
      >
        {i}
      </button>
    ));

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="sm-page">
      <div className="sm-wrap">

        {/* ── PAGE HEADER ── */}
        <div className="sm-header">
          <div>
            <p className="sm-eyebrow">Academy Operations · Admin Console</p>
            <h1 className="sm-title">Investment Transactions & Ledger</h1>
          </div>
          <button type="button" className="sm-btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus"></i> Record Transaction
          </button>
        </div>

        {/* ── KPI STRIP ── */}
        <div className="sm-kpi-grid">
          {[
            { label: "Total transactions", value: total },
            { label: "Showing records", value: transactions.length },
            { label: "Current page", value: `${currentPage} / ${totalPages}` },
          ].map(({ label, value }) => (
            <div key={label} className="sm-kpi">
              <span className="sm-kpi-label">{label}</span>
              <span className="sm-kpi-val">{loading ? <Skeleton w={40} h={22} r={5} /> : value}</span>
            </div>
          ))}
        </div>

        {/* ── TOOLBAR ── */}
        <div className="sm-toolbar">
          <div className="sm-search">
            <i className="fas fa-search sm-search-icon"></i>
            <input
              type="text"
              placeholder="Search reference, investor name, code, or investment ref..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 160 }}>
            <select className="sm-field bg-white" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All Types</option>
              <option value="investment">Investment</option>
              <option value="repayment">Repayment</option>
              <option value="profit">Profit</option>
              <option value="loss">Loss</option>
              <option value="adjustment">Adjustment</option>
            </select>
          </div>
          <div style={{ minWidth: 160 }}>
            <select className="sm-field bg-white" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="">All Branches</option>
              {globalBranches.map(b => (
                <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: 150 }}>
            <select className="sm-field bg-white" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* ── DATA TABLE ── */}
        <div className="sm-card">
          <div className="table-responsive">
            <table className="sm-table">
              <thead>
                <tr>
                  <th style={{ width: "22%" }}>Transaction Ref</th>
                  <th style={{ width: "22%" }}>Investor / Investment</th>
                  <th style={{ width: "16%" }}>Type & Amount</th>
                  <th style={{ width: "16%" }}>Breakdown Component</th>
                  <th style={{ width: "12%" }}>Status</th>
                  <th className="text-end" style={{ width: "12%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="sm-row">
                      <td>
                        <div className="sm-name-cell">
                          <Skeleton w={34} h={34} r={8} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                            <Skeleton w="65%" h={12} />
                            <Skeleton w="45%" h={10} />
                          </div>
                        </div>
                      </td>
                      <td><Skeleton w={100} h={12} /></td>
                      <td><Skeleton w={70} h={12} /></td>
                      <td><Skeleton w={80} h={12} /></td>
                      <td><Skeleton w={60} h={22} r={20} /></td>
                      <td style={{ textAlign: "right" }}><Skeleton w={72} h={30} r={7} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="sm-empty-state">
                      <div className="sm-empty-icon-wrap">
                        <i className="fas fa-exchange-alt"></i>
                      </div>
                      <p className="sm-empty-title">No investment transaction logs found</p>
                      <span className="sm-empty-desc">Try modifying your filter or record a new transaction.</span>
                    </td>
                  </tr>
                ) : (
                  transactions.map((tx) => (
                    <tr key={tx.id} className="sm-row">
                      <td>
                        <div className="sm-name-cell">
                          <div className="sm-avatar">{tx.transaction_ref?.[0]?.toUpperCase() || "T"}</div>
                          <div>
                            <div className="sm-name">{tx.transaction_ref}</div>
                            <div className="sm-loc">Date: {tx.transaction_date}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="sm-name">{tx.investor_name || "N/A"}</div>
                        <div className="sm-loc font-monospace">Inv Ref: {tx.investment_ref || "N/A"}</div>
                      </td>
                      <td>
                        <div className="sm-date-main">₹{parseFloat(tx.amount || 0).toLocaleString()}</div>
                        <div className="sm-loc text-uppercase fw-medium text-primary">{tx.transaction_type}</div>
                      </td>
                      <td>
                        <div className="sm-loc">Principal: ₹{parseFloat(tx.principal_amount || 0).toLocaleString()}</div>
                        <div className="sm-loc">Interest: ₹{parseFloat(tx.interest_amount || 0).toLocaleString()}</div>
                      </td>
                      <td>
                        <span className={`sm-pill ${
                          tx.status === 'completed' ? 'pill-green' :
                          tx.status === 'pending' ? 'pill-amber' : 'pill-coral'
                        }`}>
                          {tx.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <button type="button" className="sm-btn-outline" onClick={() => openEditModal(tx)}>
                            Edit
                          </button>
                          {/* <button type="button" className="sm-btn-outline text-danger border-danger" onClick={() => handleDelete(tx.id)}>
                            Delete
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION ── */}
          <div className="sm-footer">
            <span className="sm-count">{transactions.length} of {total} records</span>
            <div className="sm-pagination">
              <button type="button" className="sm-page-btn sm-page-arrow" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button type="button" className="sm-page-btn sm-page-arrow" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MODAL
      ═══════════════════════════════════════ */}
      {showModal && (
        <div
          className="sm-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}
        >
          <div className="sm-modal">

            {/* Sidebar */}
            <div className="sm-modal-side">
              <div className="sm-modal-side-header">
                <p className="sm-modal-side-title">Ledger Wizard</p>
                <p className="sm-modal-side-sub">Payout & Capital Logging</p>
              </div>
              <nav className="sm-modal-nav">
                {[
                  { key: "general", icon: "fas fa-info-circle", label: "General details" },
                  { key: "components", icon: "fas fa-calculator", label: "Financial split" },
                  { key: "payment", icon: "fas fa-wallet", label: "Payment & mode" },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`sm-nav-item${activeTab === key ? " active" : ""}`}
                    onClick={() => setActiveTab(key)}
                    disabled={saving}
                  >
                    <i className={icon}></i>
                    <span>{label}</span>
                  </button>
                ))}
              </nav>

              <div style={{ padding: "16px", marginTop: "auto" }}>
                <div className="sm-status-toggle-row" style={{ background: "#fff" }}>
                  <div>
                    <label className="sm-label" style={{ marginBottom: 2 }}>Status</label>
                  </div>
                  <select name="status" className="sm-field" style={{ width: "auto", padding: "4px 8px", fontSize: "11.5px" }} value={formData.status} onChange={handleChange} disabled={saving}>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Main panel */}
            <form onSubmit={handleSubmit} className="sm-modal-main">
              <div className="sm-modal-header">
                <div>
                  <h2 className="sm-modal-title">{isEditMode ? "Update Transaction Entry" : "Record New Transaction"}</h2>
                  <p className="sm-modal-sub">Ref: {formData.transaction_ref || "PENDING"}</p>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)} aria-label="Close" disabled={saving}></button>
              </div>

              <div className="sm-modal-body">
                <div className="row g-3">

                  {/* ── GENERAL DETAILS ── */}
                  {activeTab === "general" && (
                    <>
                      <div className="col-md-6">
                        <label className="sm-label">Transaction Ref *</label>
                        <input type="text" name="transaction_ref" className="sm-field" placeholder="e.g. ITXN-001" value={formData.transaction_ref} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Transaction Type *</label>
                        <select name="transaction_type" className="sm-field" value={formData.transaction_type} onChange={handleChange}>
                          <option value="investment">Investment</option>
                          <option value="repayment">Repayment</option>
                          <option value="profit">Profit</option>
                          <option value="loss">Loss</option>
                          <option value="adjustment">Adjustment</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Investment Mapping *</label>
                        <select name="investment_id" className="sm-field" value={formData.investment_id} onChange={handleChange}>
                          <option value="">Select Investment</option>
                          {globalInvestments.map(inv => (
                            <option key={inv.id} value={inv.id}>{inv.investment_ref} (₹{inv.expected_principal})</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Investor *</label>
                        <select name="investor_id" className="sm-field" value={formData.investor_id} onChange={handleChange}>
                          <option value="">Select Investor</option>
                          {globalInvestors.map(inv => (
                            <option key={inv.id} value={inv.id}>{inv.investor_name} ({inv.investor_code})</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Branch *</label>
                        <select name="branch_id" className="sm-field" value={formData.branch_id} onChange={handleChange}>
                          <option value="">Select Branch</option>
                          {globalBranches.map(b => (
                            <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Transaction Date *</label>
                        <input type="date" name="transaction_date" className="sm-field" value={formData.transaction_date} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Installment No</label>
                        <input type="number" name="installment_no" className="sm-field" value={formData.installment_no} onChange={handleChange} placeholder="e.g. 1" />
                      </div>
                    </>
                  )}

                  {/* ── COMPONENTS & FINANCIAL SPLIT ── */}
                  {activeTab === "components" && (
                    <>
                      <div className="col-md-6">
                        <label className="sm-label">Total Amount (₹) *</label>
                        <input type="number" step="0.01" name="amount" className="sm-field fw-bold text-primary" value={formData.amount} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Principal Amount (₹)</label>
                        <input type="number" step="0.01" name="principal_amount" className="sm-field" value={formData.principal_amount} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Interest Amount (₹)</label>
                        <input type="number" step="0.01" name="interest_amount" className="sm-field" value={formData.interest_amount} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Profit Amount (₹)</label>
                        <input type="number" step="0.01" name="profit_amount" className="sm-field" value={formData.profit_amount} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Loss Amount (₹)</label>
                        <input type="number" step="0.01" name="loss_amount" className="sm-field" value={formData.loss_amount} onChange={handleChange} />
                      </div>
                    </>
                  )}

                  {/* ── PAYMENT & MODE ── */}
                  {activeTab === "payment" && (
                    <>
                      <div className="col-md-6">
                        <label className="sm-label">Payment Method</label>
                        <select name="payment_method" className="sm-field" value={formData.payment_method} onChange={handleChange}>
                          <option value="">Select Method</option>
                          <option value="bank_transfer">Bank Transfer / NEFT / RTGS</option>
                          <option value="upi">UPI / QR Code</option>
                          <option value="cheque">Cheque</option>
                          <option value="cash">Cash</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Bank Account No / Identifier</label>
                        <input type="text" name="account_no" className="sm-field" value={formData.account_no} onChange={handleChange} placeholder="e.g. ACC-5542" />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">External Reference No / UTR</label>
                        <input type="text" name="reference_no" className="sm-field" value={formData.reference_no} onChange={handleChange} placeholder="e.g. UTR123456789" />
                      </div>
                      <div className="col-12">
                        <label className="sm-label">Description / Remarks</label>
                        <textarea name="description" rows="3" className="sm-field" value={formData.description} onChange={handleChange} placeholder="Notes or payment particulars..." />
                      </div>
                    </>
                  )}

                </div>
              </div>

              {/* ── MODAL FOOTER with loader ── */}
              <div className="sm-modal-footer">
                <button type="button" className="sm-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                  Discard
                </button>
                <button type="submit" className="sm-btn-primary sm-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      {isEditMode ? "Updating…" : "Recording…"}
                    </>
                  ) : (
                    isEditMode ? "Update Transaction" : "Save Transaction"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        @keyframes sm-spin { to { transform: rotate(360deg); } }
        @keyframes sm-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes sm-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes sm-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        /* ── BASE ── */
        .sm-page {
          background: #f5f5f7;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          color: #1d1d1f;
          font-size: 14px;
        }
        .sm-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 1.5rem; }

        /* ── PAGE HEADER ── */
        .sm-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .sm-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #aeaeb2; margin: 0 0 4px; }
        .sm-title { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.022em; margin: 0; }

        /* ── KPI STRIP ── */
        .sm-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 1.25rem; }
        @media (max-width: 640px) { .sm-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        .sm-kpi { background: #fff; border: 1px solid #d2d2d7; border-radius: 12px; padding: 14px 18px; display: flex; flex-direction: column; gap: 5px; }
        .sm-kpi-label { font-size: 11px; color: #6e6e73; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .sm-kpi-val { font-size: 1.5rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.025em; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        /* ── TOOLBAR ── */
        .sm-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; flex-wrap: wrap; }
        .sm-search { flex: 1; min-width: 200px; position: relative; }
        .sm-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aeaeb2; font-size: 12px; }
        .sm-search input {
          width: 100%; background: #fff; border: 1px solid #d2d2d7; border-radius: 10px;
          padding: 9px 12px 9px 34px; font-size: 13px; color: #1d1d1f; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .sm-search input:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .sm-search input::placeholder { color: #aeaeb2; }

        /* ── PRIMARY BUTTON ── */
        .sm-btn-primary {
          background: #0071e3; color: #fff; border: none; border-radius: 10px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
          transition: background 0.15s, opacity 0.15s; white-space: nowrap; font-family: inherit;
          min-width: 140px; justify-content: center;
        }
        .sm-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .sm-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .sm-btn-submit { padding: 9px 22px; min-width: 158px; }

        /* ── TABLE CARD ── */
        .sm-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; overflow: hidden; }
        .sm-table { width: 100%; border-collapse: collapse; }
        .sm-table thead th {
          background: #f5f5f7; font-size: 10.5px; font-weight: 600; color: #6e6e73;
          text-transform: uppercase; letter-spacing: 0.07em; padding: 11px 18px;
          border-bottom: 1px solid #e8e8ed; white-space: nowrap;
        }
        .sm-table tbody tr { border-bottom: 1px solid #f5f5f7; transition: background 0.1s; }
        .sm-table tbody tr:last-child { border-bottom: none; }
        .sm-row:hover { background: #fafafa; }
        .sm-table td { padding: 13px 18px; vertical-align: middle; }

        .sm-name-cell { display: flex; align-items: center; gap: 10px; }
        .sm-avatar {
          width: 34px; height: 34px; border-radius: 8px; background: #f5f5f7; border: 1px solid #e8e8ed;
          color: #3c3c43; font-size: 13px; font-weight: 600; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .sm-name { font-size: 13px; font-weight: 500; color: #1d1d1f; }
        .sm-loc { font-size: 11.5px; color: #6e6e73; margin-top: 2px; }

        /* ── PILLS ── */
        .sm-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; white-space: nowrap; }
        .pill-amber { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
        .pill-green { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .pill-blue  { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
        .pill-coral { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

        .sm-date-main { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }

        /* ── OUTLINE BUTTON ── */
        .sm-btn-outline {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; padding: 6px 12px;
          font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .sm-btn-outline:hover { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }

        /* ── EMPTY STATE ── */
        .sm-empty-state { text-align: center; padding: 3rem 1rem; }
        .sm-empty-icon-wrap {
          width: 52px; height: 52px; background: #f5f5f7; border-radius: 14px;
          display: inline-flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #aeaeb2; margin-bottom: 12px;
        }
        .sm-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .sm-empty-desc { font-size: 12px; color: #aeaeb2; }

        /* ── PAGINATION ── */
        .sm-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-top: 1px solid #f5f5f7; }
        .sm-count { font-size: 12px; color: #6e6e73; font-weight: 500; }
        .sm-pagination { display: flex; gap: 4px; }
        .sm-page-btn {
          min-width: 30px; height: 30px; padding: 0 6px; border: 1px solid #e8e8ed; background: #fff;
          border-radius: 7px; font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center; transition: all 0.12s; font-family: inherit;
        }
        .sm-page-btn:hover:not(:disabled):not(.active) { background: #f5f5f7; border-color: #d2d2d7; }
        .sm-page-btn.active { background: #0071e3; color: #fff; border-color: #0071e3; }
        .sm-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        /* ── MODAL ── */
        .sm-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: sm-overlay-in 0.2s ease;
        }
        .sm-modal {
          width: 100%; max-width: 900px; height: 590px; background: #fff; border-radius: 18px; border: 1px solid #d2d2d7;
          box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.06);
          display: flex; overflow: hidden; animation: sm-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1);
        }

        /* Sidebar */
        .sm-modal-side { width: 210px; background: #f5f5f7; border-right: 1px solid #e8e8ed; display: flex; flex-direction: column; flex-shrink: 0; }
        .sm-modal-side-header { padding: 18px 16px; border-bottom: 1px solid #e8e8ed; }
        .sm-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .sm-modal-side-sub { font-size: 11px; color: #aeaeb2; margin: 3px 0 0; }
        .sm-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .sm-nav-item {
          display: flex; align-items: center; gap: 9px; padding: 9px 12px; border-radius: 9px; border: none; background: transparent;
          font-size: 13px; color: #6e6e73; font-weight: 400; cursor: pointer; text-align: left; width: 100%;
          transition: all 0.12s; position: relative; font-family: inherit;
        }
        .sm-nav-item i { font-size: 12.5px; width: 14px; text-align: center; flex-shrink: 0; }
        .sm-nav-item span { flex: 1; }
        .sm-nav-item:hover:not(.active):not(:disabled) { background: #ebebf0; color: #3c3c43; }
        .sm-nav-item.active { background: #fff; color: #0071e3; font-weight: 500; border: 1px solid #e8e8ed; }
        .sm-nav-item:disabled { opacity: 0.5; cursor: not-allowed; }

        /* Main panel */
        .sm-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .sm-modal-header { padding: 18px 26px; border-bottom: 1px solid #f5f5f7; display: flex; align-items: flex-start; justify-content: space-between; }
        .sm-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; letter-spacing: -0.015em; }
        .sm-modal-sub { font-size: 12px; color: #6e6e73; margin: 3px 0 0; }
        .sm-modal-body { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .sm-modal-footer { padding: 14px 26px; border-top: 1px solid #f5f5f7; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f5f5f7; }

        /* ── Form fields ── */
        .sm-label { display: block; font-size: 11px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
        .sm-field {
          width: 100%; border: 1px solid #d2d2d7; border-radius: 9px; padding: 9px 12px; font-size: 13px; color: #1d1d1f;
          background: #fff; outline: none; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .sm-field:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .sm-field::placeholder { color: #aeaeb2; }
        select.sm-field { cursor: pointer; }

        .sm-status-toggle-row {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 9px; padding: 12px 16px;
        }

        /* ── CANCEL BUTTON ── */
        .sm-btn-cancel {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 9px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; color: #6e6e73; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .sm-btn-cancel:hover:not(:disabled) { background: #f5f5f7; color: #3c3c43; }
        .sm-btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

        /* ── MODAL BODY SCROLLBAR ── */
        .sm-modal-body::-webkit-scrollbar { width: 5px; }
        .sm-modal-body::-webkit-scrollbar-track { background: transparent; }
        .sm-modal-body::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 3px; }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .sm-modal { flex-direction: column; height: auto; max-height: 90vh; }
          .sm-modal-side { width: 100%; border-right: none; border-bottom: 1px solid #e8e8ed; }
          .sm-modal-side-header { display: none; }
          .sm-modal-nav { flex-direction: row; overflow-x: auto; padding: 6px; scrollbar-width: none; }
          .sm-modal-nav::-webkit-scrollbar { display: none; }
          .sm-nav-item { white-space: nowrap; width: auto; font-size: 12px; padding: 7px 12px; }
          .sm-modal-body { padding: 16px; }
          .sm-modal-footer { padding: 12px 16px; }
          .sm-modal-header { padding: 14px 16px; }
        }
        @media (max-width: 480px) {
          .sm-wrap { padding: 1rem; }
          .sm-header { flex-direction: column; }
          .sm-btn-primary { width: 100%; justify-content: center; }
          .sm-toolbar { flex-direction: column; }
          .sm-modal-footer { flex-wrap: wrap; }
          .sm-btn-submit { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default InvestmentTransactions;